if (!customElements.get('fit-finder')) {
  const CM_PER_INCH = 2.54;
  const STORAGE_KEY = 'fit-finder:measurements';

  // Body chest circumference estimate: treating the torso as a cylinder, weight ∝ height × girth²,
  // so girth ∝ √(weight / height). 151 calibrates it to ~100 cm for 178 cm / 78 kg.
  const CHEST_FACTOR = 151;

  // Same model for the body waist where trousers sit; 130 calibrates it to ~86 cm for 178 cm / 78 kg.
  const WAIST_FACTOR = 130;

  // The scale runs Slim (0) → Regular (0.5) → Oversized (1). A result within this distance of a
  // label reads as that fit; anything else reads as "between" the two neighbouring labels.
  const LABEL_RADIUS = 0.125;

  class FitFinder extends HTMLElement {
    // The element lives inside a modal-dialog, which moves itself to <body> on connect, so this runs twice.
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-step-panel="measure"]');
      this.resultPanel = this.querySelector('[data-step-panel="result"]');
      this.heightInput = this.querySelector('input[name="height"]');
      this.weightInput = this.querySelector('input[name="weight"]');
      this.resultTitle = this.querySelector('.fit-finder__result-title');
      this.marker = this.querySelector('.fit-finder__marker');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.backButton = this.querySelector('[data-back]');

      try {
        this.messages = JSON.parse(this.querySelector('[data-fit-finder-messages]').textContent);
      } catch (e) {
        this.messages = {};
      }

      // Trousers are fitted on the waist; jackets, shirts and tracksuits (fitted on the jacket) on the chest.
      this.bodyFactor = this.dataset.garmentType === 'trousers' ? WAIST_FACTOR : CHEST_FACTOR;

      const unitFactor = this.dataset.garmentUnit === 'in' ? CM_PER_INCH : 1;
      const widthMultiplier = this.dataset.widthMeasurement === 'circumference' ? 1 : 2;
      this.garmentGirth = parseFloat(this.dataset.garmentWidth) * unitFactor * widthMultiplier;

      // Ease (cm) = garment girth − body girth at which the piece reads as slim / regular / oversized.
      const slim = Number(this.dataset.easeSlim) || 0;
      const regular = Math.max(Number(this.dataset.easeRegular) || 0, slim + 1);
      const oversized = Math.max(Number(this.dataset.easeOversized) || 0, regular + 1);
      this.anchors = [slim, regular, oversized];

      this.restoreMeasurements();
      this.updateSliders();

      [this.heightInput, this.weightInput].forEach((input) => {
        input.addEventListener('input', this.updateSliders.bind(this));
      });
      this.form.addEventListener('submit', this.onCheckFit.bind(this));
      this.backButton.addEventListener('click', this.showStep.bind(this, 'measure'));
      this.addButton.addEventListener('click', this.onAddToCart.bind(this));

      this.connectProductForm();
    }

    disconnectedCallback() {
      // Ignore the move to <body>; only clean up once the element is really gone.
      setTimeout(() => {
        if (!this.isConnected && this.submitObserver) this.submitObserver.disconnect();
      });
    }

    get height() {
      return Number(this.heightInput.value);
    }

    get weight() {
      return Number(this.weightInput.value);
    }

    get modal() {
      return this.closest('modal-dialog');
    }

    /* ---------------------------------------------------------------------------------------------
     * Step 1: height and weight
     * ------------------------------------------------------------------------------------------- */

    // Paints the filled part of each track and keeps the value readouts in sync.
    updateSliders() {
      [this.heightInput, this.weightInput].forEach((input) => {
        const min = Number(input.min);
        const max = Number(input.max);
        const progress = max > min ? ((Number(input.value) - min) / (max - min)) * 100 : 0;
        input.style.setProperty('--progress', `${progress}%`);
        input.setAttribute('aria-valuetext', `${input.value} ${input.dataset.unit}`);

        const output = this.querySelector(`output[for="${input.id}"] [data-value]`);
        if (output) output.textContent = input.value;
      });
    }

    onCheckFit(event) {
      event.preventDefault();
      if (!(this.garmentGirth > 0) || !this.height || !this.weight) return;

      this.saveMeasurements();

      const bodyGirth = this.bodyFactor * Math.sqrt(this.weight / this.height);
      const ease = this.garmentGirth - bodyGirth;
      this.renderResult(this.position(ease));
      this.showStep('result');
    }

    /* ---------------------------------------------------------------------------------------------
     * Step 2: fit result
     * ------------------------------------------------------------------------------------------- */

    // Maps the ease onto the scale: 0 at the slim anchor, 0.5 at regular, 1 at oversized,
    // interpolating linearly between them and clamping at both ends.
    position(ease) {
      const [slim, regular, oversized] = this.anchors;
      const value =
        ease <= regular ? (0.5 * (ease - slim)) / (regular - slim) : 0.5 + (0.5 * (ease - regular)) / (oversized - regular);
      return Math.min(Math.max(value, 0), 1);
    }

    describe(position) {
      if (position <= LABEL_RADIUS) return { key: 'slim', fit: 'slim' };
      if (position < 0.5 - LABEL_RADIUS) return { key: 'slimRegular' };
      if (position <= 0.5 + LABEL_RADIUS) return { key: 'regular', fit: 'regular' };
      if (position < 1 - LABEL_RADIUS) return { key: 'regularOversized' };
      return { key: 'oversized', fit: 'oversized' };
    }

    renderResult(position) {
      const { key, fit } = this.describe(position);
      this.resultTitle.textContent = this.messages[key] || '';
      this.querySelectorAll('[data-fit]').forEach((label) => {
        label.classList.toggle('is-active', label.dataset.fit === fit);
      });

      // Let the panel render at the previous position first so the marker glides to the new one.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.marker.style.setProperty('--position', `${(position * 100).toFixed(1)}%`);
        });
      });
    }

    // On the result step the heading gives way to the result title (see component-fit-finder.css).
    showStep(step) {
      this.dataset.step = step;
      this.form.hidden = step !== 'measure';
      this.resultPanel.hidden = step !== 'result';

      if (step === 'result') {
        this.resultTitle.focus({ preventScroll: true });
      } else {
        this.heightInput.focus({ preventScroll: true });
      }
    }

    // The "Add to cart" button hands off to the product form of this section, so the cart drawer,
    // sold-out state and variant selection keep working exactly as they do for the main button.
    get productSubmitButton() {
      return document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
    }

    connectProductForm() {
      const submitButton = this.productSubmitButton;
      if (!submitButton) {
        this.addButton.hidden = true;
        return;
      }

      this.syncAddButton();
      this.submitObserver = new MutationObserver(this.syncAddButton.bind(this));
      this.submitObserver.observe(submitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    // Mirrors the main button: its label when it can't add (Sold out / Unavailable), its loading state,
    // and our own label otherwise.
    syncAddButton() {
      const submitButton = this.productSubmitButton;
      if (!submitButton) return;

      const disabled = submitButton.disabled;
      const loading = submitButton.classList.contains('loading');
      const label = this.addButton.querySelector('span');
      const spinner = this.addButton.querySelector('.loading__spinner');

      this.addButton.disabled = disabled;
      this.addButton.classList.toggle('loading', loading);
      this.addButton.setAttribute('aria-disabled', disabled || loading);
      if (spinner) spinner.classList.toggle('hidden', !loading);
      if (label) {
        label.textContent = disabled ? submitButton.textContent.trim() : this.messages.addToCart || '';
      }
    }

    onAddToCart() {
      const submitButton = this.productSubmitButton;
      if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;
      const modal = this.modal;
      if (modal && modal.hasAttribute('open')) modal.hide();
      submitButton.click();
    }

    saveMeasurements() {
      let values = {};
      try {
        values = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch (e) {}

      values.height_cm = this.heightInput.value;
      values.weight_kg = this.weightInput.value;

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
      } catch (e) {}
    }

    restoreMeasurements() {
      let values;
      try {
        values = JSON.parse(localStorage.getItem(STORAGE_KEY));
      } catch (e) {}
      if (!values) return;

      const apply = (input, raw) => {
        const value = Math.round(parseFloat(String(raw || '').replace(',', '.')));
        if (!Number.isFinite(value)) return;
        input.value = Math.min(Math.max(value, Number(input.min)), Number(input.max));
      };

      apply(this.heightInput, values.height_cm);
      apply(this.weightInput, values.weight_kg);
    }
  }

  customElements.define('fit-finder', FitFinder);

  // Theme editor: selecting the block opens its modal so the merchant can see what they edit.
  if (window.Shopify && window.Shopify.designMode) {
    const modalFor = (target) => {
      const opener = target && target.closest && target.closest('[data-fit-finder-block]');
      return opener ? { opener, modal: document.querySelector(opener.getAttribute('data-modal')) } : null;
    };
    document.addEventListener('shopify:block:select', (event) => {
      const found = modalFor(event.target);
      if (found && found.modal && !found.modal.hasAttribute('open')) found.modal.show(found.opener.querySelector('button'));
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const found = modalFor(event.target);
      if (found && found.modal && found.modal.hasAttribute('open')) found.modal.hide();
    });
  }
}
